/**
 * WB4-IE1 — Inline edit field registry SSOT.
 *
 * domain × fieldKey × riskTier × editorType × actionId/handlerRef (via interaction contract).
 * UI reads editable keys from here; high/finance fields stay off the editable set.
 *
 * GAP-15 matrix inventory marker — `check:workbench-component-inventory` ·
 * `scripts/lib/workbench-component-inventory.ts`.
 */
export const WORKBENCH_INLINE_FIELD_REGISTRY_MATRIX_INVENTORY_MARKER =
  'GAP-15-inline-field-inventory' as const;

import type { WorkbenchRiskTier } from './workbench-action-safety-policy';
import { buildActionSafety, type WorkbenchActionSafety } from './workbench-action-safety-policy';
import {
  buildInlineEditActionId,
  buildInlineUpdateHandlerRef,
} from './workbench-interaction-surface-contract';

export type InlineFieldEditorType = 'text' | 'textarea' | 'enum' | 'date' | 'user';

export interface InlineFieldEnumOption {
  value: string;
  label: string;
}

/** Default enum options for priority / reorderPriority inline fields. */
export const WORKBENCH_INLINE_FIELD_PRIORITY_ENUM_OPTIONS: readonly InlineFieldEnumOption[] = [
  { value: 'low', label: '낮음' },
  { value: 'medium', label: '보통' },
  { value: 'high', label: '높음' },
  { value: 'urgent', label: '긴급' },
] as const;

/** TaskExecutionStatus — Process Layer task runtime states. */
export const WORKBENCH_INLINE_FIELD_TASK_EXECUTION_STATUS_ENUM_OPTIONS: readonly InlineFieldEnumOption[] =
  [
    { value: 'pending', label: '대기' },
    { value: 'assigned', label: '배정됨' },
    { value: 'in_progress', label: '진행 중' },
    { value: 'approved', label: '승인됨' },
    { value: 'rejected', label: '반려됨' },
    { value: 'completed', label: '완료' },
    { value: 'skipped', label: '건너뜀' },
  ] as const;

/** TaskAssignment status — assignment_execution list inline edit. */
export const WORKBENCH_INLINE_FIELD_ASSIGNMENT_STATUS_ENUM_OPTIONS: readonly InlineFieldEnumOption[] =
  [
    { value: 'active', label: '활성' },
    { value: 'inactive', label: '비활성' },
    { value: 'transferred', label: '이관됨' },
    { value: 'completed', label: '완료' },
  ] as const;

const PRIORITY_ENUM_FIELD_KEYS = new Set(['priority', 'reorderPriority']);

export interface WorkbenchInlineFieldRegistryEntry {
  fieldKey: string;
  label: string;
  businessDomains: readonly string[];
  riskTier: WorkbenchRiskTier;
  editorType: InlineFieldEditorType;
  /** Table column key when it differs from persist fieldKey (e.g. displayName → name). */
  columnKey?: string;
  /** Enum editor options — when omitted, priority fields use WORKBENCH_INLINE_FIELD_PRIORITY_ENUM_OPTIONS. */
  enumOptions?: readonly InlineFieldEnumOption[];
}

export interface WorkbenchInlineFieldBlockedEntry {
  fieldKey: string;
  label: string;
  businessDomains: readonly string[];
  reason: string;
}

/** Tier 1–2 operational fields — inline_edit surface eligible. */
export const WORKBENCH_INLINE_FIELD_REGISTRY: readonly WorkbenchInlineFieldRegistryEntry[] = [
  {
    fieldKey: 'description',
    label: '설명',
    businessDomains: ['sellable_item', 'raw_material_item'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'name',
    label: '표시명',
    businessDomains: ['sellable_item'],
    riskTier: 'low',
    editorType: 'text',
    columnKey: 'name',
  },
  {
    fieldKey: 'internalNote',
    label: '내부 메모',
    businessDomains: ['sellable_item'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'tags',
    label: '태그',
    businessDomains: ['sellable_item'],
    riskTier: 'low',
    editorType: 'text',
  },
  {
    fieldKey: 'priority',
    label: '우선순위',
    businessDomains: ['sellable_item'],
    riskTier: 'medium',
    editorType: 'enum',
  },
  {
    fieldKey: 'storageNote',
    label: '보관 메모',
    businessDomains: ['raw_material_item'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'supplierNote',
    label: '공급처 메모',
    businessDomains: ['raw_material_item'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'assignee',
    label: '담당자',
    businessDomains: ['production_order'],
    riskTier: 'medium',
    editorType: 'user',
  },
  {
    fieldKey: 'assignedToEntityId',
    label: '담당자',
    businessDomains: ['assignment_execution', 'task_execution'],
    riskTier: 'medium',
    editorType: 'user',
    columnKey: 'assignee',
  },
  {
    fieldKey: 'dueDate',
    label: '마감일',
    businessDomains: ['production_order'],
    riskTier: 'medium',
    editorType: 'date',
  },
  {
    fieldKey: 'deadline',
    label: '마감일',
    businessDomains: ['assignment_execution', 'task_execution'],
    riskTier: 'medium',
    editorType: 'date',
    columnKey: 'dueDate',
  },
  {
    fieldKey: 'priority',
    label: '우선순위',
    businessDomains: [
      'assignment_execution',
      'production_order',
      'task_execution',
      'order',
      'sales_order',
    ],
    riskTier: 'medium',
    editorType: 'enum',
    columnKey: 'priority',
  },
  {
    fieldKey: 'status',
    label: '업무 상태',
    businessDomains: ['task_execution'],
    riskTier: 'medium',
    editorType: 'enum',
    enumOptions: WORKBENCH_INLINE_FIELD_TASK_EXECUTION_STATUS_ENUM_OPTIONS,
  },
  {
    fieldKey: 'status',
    label: '배정 상태',
    businessDomains: ['assignment_execution'],
    riskTier: 'medium',
    editorType: 'enum',
    enumOptions: WORKBENCH_INLINE_FIELD_ASSIGNMENT_STATUS_ENUM_OPTIONS,
  },
  {
    fieldKey: 'quantity',
    label: '수량',
    businessDomains: ['order', 'sales_order'],
    riskTier: 'medium',
    editorType: 'text',
  },
  {
    fieldKey: 'assignee',
    label: '담당자',
    businessDomains: ['order', 'sales_order'],
    riskTier: 'medium',
    editorType: 'user',
  },
  {
    fieldKey: 'dueDate',
    label: '마감일',
    businessDomains: ['order', 'sales_order'],
    riskTier: 'medium',
    editorType: 'date',
    columnKey: 'deliveryDate',
  },
  {
    fieldKey: 'note',
    label: '메모',
    businessDomains: ['order', 'sales_order'],
    riskTier: 'low',
    editorType: 'textarea',
    columnKey: 'internalNote',
  },
  {
    fieldKey: 'paymentDate',
    label: '결제일',
    businessDomains: ['finance_state', 'finance_flow', 'stream_execution'],
    riskTier: 'medium',
    editorType: 'date',
  },
  {
    fieldKey: 'location',
    label: '위치',
    businessDomains: ['inventory_shortage', 'inventory_item', 'warehouse_stock'],
    riskTier: 'medium',
    editorType: 'text',
  },
  {
    fieldKey: 'quantity',
    label: '수량',
    businessDomains: ['inventory_shortage', 'inventory_item', 'warehouse_stock'],
    riskTier: 'medium',
    editorType: 'text',
  },
  {
    fieldKey: 'contact',
    label: '연락처',
    businessDomains: ['internal_entity', 'external_entity', 'entity'],
    riskTier: 'medium',
    editorType: 'text',
  },
  {
    fieldKey: 'owner',
    label: '소유자',
    businessDomains: ['internal_entity', 'external_entity', 'entity'],
    riskTier: 'medium',
    editorType: 'user',
  },
  {
    fieldKey: 'deliveryDate',
    label: '납기일',
    businessDomains: ['production_order'],
    riskTier: 'medium',
    editorType: 'date',
  },
  {
    fieldKey: 'internalNote',
    label: '내부 메모',
    businessDomains: ['production_order'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'accountManager',
    label: '계정 담당자',
    businessDomains: ['customer', 'counterparty', 'external_entity'],
    riskTier: 'medium',
    editorType: 'user',
  },
  {
    fieldKey: 'followUpDate',
    label: '팔로업 일자',
    businessDomains: ['customer', 'counterparty', 'external_entity'],
    riskTier: 'medium',
    editorType: 'date',
  },
  {
    fieldKey: 'collectionNote',
    label: '수금 메모',
    businessDomains: ['customer', 'counterparty', 'external_entity'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'picker',
    label: '피킹 담당자',
    businessDomains: ['warehouse_picking_list'],
    riskTier: 'medium',
    editorType: 'user',
  },
  {
    fieldKey: 'pickingNote',
    label: '피킹 메모',
    businessDomains: ['warehouse_picking_list'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'locationNote',
    label: '위치 메모',
    businessDomains: ['warehouse_picking_list'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'internalNote',
    label: '내부 메모',
    businessDomains: ['finance_state', 'finance_flow', 'stream_execution'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'assignee',
    label: '담당자',
    businessDomains: ['finance_state', 'finance_flow', 'stream_execution'],
    riskTier: 'medium',
    editorType: 'user',
  },
  {
    fieldKey: 'dueDate',
    label: '만기일',
    businessDomains: ['finance_state'],
    riskTier: 'medium',
    editorType: 'date',
  },
  {
    fieldKey: 'followUpDate',
    label: '팔로업 일자',
    businessDomains: ['collection', 'ar_collection'],
    riskTier: 'medium',
    editorType: 'date',
    /** finance/receivables proxy surface column */
    columnKey: 'dueDate',
  },
  {
    fieldKey: 'assignee',
    label: '수금 담당자',
    businessDomains: ['collection', 'ar_collection'],
    riskTier: 'medium',
    editorType: 'user',
    columnKey: 'assignee',
  },
  {
    fieldKey: 'collectionNote',
    label: '수금 메모',
    businessDomains: ['collection', 'ar_collection'],
    riskTier: 'low',
    editorType: 'textarea',
    /** finance/receivables proxy surface column */
    columnKey: 'internalNote',
  },
  {
    fieldKey: 'replenishmentNote',
    label: '보충 메모',
    businessDomains: ['inventory_shortage'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'reorderPriority',
    label: '재주문 우선순위',
    businessDomains: ['inventory_shortage'],
    riskTier: 'medium',
    editorType: 'enum',
    columnKey: 'priority',
  },
  {
    fieldKey: 'documentNote',
    label: '문서 메모',
    businessDomains: [
      'knowledge_report',
      'knowledge_policy',
      'knowledge_plan',
      'document',
      'document_report',
      'document_policy',
      'document_plan',
      'org_document',
    ],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'relationshipNote',
    label: '관계 메모',
    businessDomains: ['supplier'],
    riskTier: 'low',
    editorType: 'textarea',
  },
  {
    fieldKey: 'procurementLeadDays',
    label: '조달 리드타임',
    businessDomains: ['supplier'],
    riskTier: 'medium',
    editorType: 'date',
    columnKey: 'leadTime',
  },
] as const;

/** Tier 4 — never inline editable; surfaced for disabled_with_reason UX later. */
export const WORKBENCH_INLINE_FIELD_BLOCKED: readonly WorkbenchInlineFieldBlockedEntry[] = [
  {
    fieldKey: 'unitPrice',
    label: '단가',
    businessDomains: ['sellable_item', 'raw_material_item'],
    reason: '가격 필드는 inline edit가 허용되지 않습니다. 승인된 수정 경로를 사용하세요.',
  },
  {
    fieldKey: 'creditLimit',
    label: '신용 한도',
    businessDomains: ['sellable_item'],
    reason: '재무/신용 필드는 inline edit가 허용되지 않습니다.',
  },
  {
    fieldKey: 'approvedAmount',
    label: '승인 금액',
    businessDomains: ['sellable_item', 'raw_material_item'],
    reason: '승인된 재무 필드는 inline edit가 허용되지 않습니다.',
  },
  {
    fieldKey: 'amount',
    label: '금액',
    businessDomains: ['finance_state', 'finance_flow', 'stream_execution'],
    reason: '금액 필드는 inline edit가 허용되지 않습니다. 승인된 수정 경로를 사용하세요.',
  },
] as const;

export function listInlineEditRegistryDomains(): readonly string[] {
  return [...new Set(WORKBENCH_INLINE_FIELD_REGISTRY.flatMap((entry) => entry.businessDomains))];
}

/** Domains with inline_edit registry rows (task/order operational fields included). */
export function isInlineEditDomainRegistryEnabled(businessDomain: string): boolean {
  return resolveInlineFieldRegistryEntries(businessDomain).length > 0;
}

export function resolveInlineFieldRegistryEntries(
  businessDomain: string
): readonly WorkbenchInlineFieldRegistryEntry[] {
  return WORKBENCH_INLINE_FIELD_REGISTRY.filter((entry) =>
    entry.businessDomains.includes(businessDomain)
  );
}

export function resolveInlineEditFieldKeys(businessDomain: string): readonly string[] {
  return resolveInlineFieldRegistryEntries(businessDomain).map(
    (entry) => entry.columnKey ?? entry.fieldKey
  );
}

export function resolveInlineFieldRegistryEntry(
  businessDomain: string,
  fieldKey: string
): WorkbenchInlineFieldRegistryEntry | undefined {
  return resolveInlineFieldRegistryEntries(businessDomain).find(
    (entry) => entry.fieldKey === fieldKey || entry.columnKey === fieldKey
  );
}

export function resolveInlineFieldLabel(businessDomain: string, fieldKey: string): string {
  return (
    resolveInlineFieldRegistryEntry(businessDomain, fieldKey)?.label ??
    resolveInlineFieldBlockedEntry(businessDomain, fieldKey)?.label ??
    fieldKey
  );
}

export function resolveInlineFieldBlockedEntry(
  businessDomain: string,
  fieldKey: string
): WorkbenchInlineFieldBlockedEntry | undefined {
  return WORKBENCH_INLINE_FIELD_BLOCKED.find(
    (entry) => entry.fieldKey === fieldKey && entry.businessDomains.includes(businessDomain)
  );
}

export function isInlineFieldEditable(businessDomain: string, fieldKey: string): boolean {
  if (resolveInlineFieldBlockedEntry(businessDomain, fieldKey)) {
    return false;
  }
  return resolveInlineFieldRegistryEntry(businessDomain, fieldKey) !== undefined;
}

export function resolveInlineFieldBlockedReason(
  businessDomain: string,
  fieldKey: string
): string | undefined {
  return resolveInlineFieldBlockedEntry(businessDomain, fieldKey)?.reason;
}

export function resolveInlineFieldSafety(
  businessDomain: string,
  fieldKey: string
): WorkbenchActionSafety {
  const entry = resolveInlineFieldRegistryEntry(businessDomain, fieldKey);
  if (!entry) {
    return buildActionSafety('high', ['table-row'], {
      recoverabilityMode: 'approval_correction',
      confirmation: 'confirm',
    });
  }

  const recoverabilityMode =
    entry.riskTier === 'low' ? 'toast_undo' : ('revision_restore' as const);

  return buildActionSafety(entry.riskTier, ['table-row'], {
    recoverabilityMode,
    confirmation: entry.riskTier === 'medium' ? 'confirm' : 'none',
  });
}

export function listInlineEditActionIdsForDomain(businessDomain: string): readonly string[] {
  return resolveInlineFieldRegistryEntries(businessDomain).map((entry) =>
    buildInlineEditActionId(entry.fieldKey)
  );
}

export function listInlineEditHandlerRefsForDomain(businessDomain: string): readonly string[] {
  return resolveInlineFieldRegistryEntries(businessDomain).map((entry) =>
    buildInlineUpdateHandlerRef(entry.fieldKey)
  );
}

/** All inline_edit:* action ids enrolled in parity matrix (sellable + raw-material IE1). */
export const INTERACTION_SURFACE_INLINE_EDIT_PILOT_ACTION_IDS = [
  ...new Set(
    WORKBENCH_INLINE_FIELD_REGISTRY.map((entry) => buildInlineEditActionId(entry.fieldKey))
  ),
] as const;

export type InteractionSurfaceInlineEditPilotActionId =
  (typeof INTERACTION_SURFACE_INLINE_EDIT_PILOT_ACTION_IDS)[number];

/** Persist patch key — columnKey may differ from stored fieldKey only when columnKey is alias. */
export function resolveInlineFieldPersistKey(
  businessDomain: string,
  columnOrFieldKey: string
): string {
  const entry = resolveInlineFieldRegistryEntry(businessDomain, columnOrFieldKey);
  return entry?.fieldKey ?? columnOrFieldKey;
}

export function resolveInlineFieldEnumOptions(
  businessDomain: string,
  fieldKey: string
): readonly InlineFieldEnumOption[] | undefined {
  const entry = resolveInlineFieldRegistryEntry(businessDomain, fieldKey);
  if (!entry || entry.editorType !== 'enum') {
    return undefined;
  }
  if (entry.enumOptions?.length) {
    return entry.enumOptions;
  }
  if (PRIORITY_ENUM_FIELD_KEYS.has(entry.fieldKey)) {
    return WORKBENCH_INLINE_FIELD_PRIORITY_ENUM_OPTIONS;
  }
  return undefined;
}

export function resolveInlineFieldEnumLabel(
  businessDomain: string,
  fieldKey: string,
  rawValue: string
): string {
  const options = resolveInlineFieldEnumOptions(businessDomain, fieldKey);
  if (!options?.length) {
    return rawValue;
  }
  return options.find((option) => option.value === rawValue)?.label ?? rawValue;
}
